/**
 * Keep API / socket / platform URLs on the page origin for live Danzflo hosts.
 * Bitbucket builds often bake sslip.io, raw IPs, or .net↔.com mismatches into
 * REACT_APP_* — those break browsers (Failed to fetch → axios "Network Error").
 */

function stripWww(host = "") {
  return String(host || "")
    .toLowerCase()
    .replace(/^www\./, "");
}

/** Public site hosts where nginx proxies /event/api/v2 and /socket.io. */
export function isDanzfloSiteHost(host) {
  const h = stripWww(host);
  if (!h) return false;
  return /^(?:dev\.)?danzflo\.(com|net)$/.test(h) || /\.sslip\.io$/i.test(h);
}

function isMisbakedHost(host) {
  const h = stripWww(host);
  if (!h) return false;
  if (/\.sslip\.io$/i.test(h)) return true;
  if (/^\d{1,3}(?:\.\d{1,3}){3}$/.test(h)) return true;

  if (typeof window === "undefined") return false;
  const page = stripWww(window.location.hostname);

  // On a Danzflo page, any other host from env is wrong (cross .com/.net, staging IP, etc.).
  if (isDanzfloSiteHost(page) && h !== page) {
    return true;
  }

  return false;
}

export function resolveApiUrl(url = "") {
  const raw = String(url || "");
  if (!raw || typeof window === "undefined") {
    return raw;
  }

  try {
    const absolute = new URL(raw, window.location.origin);
    if (!isMisbakedHost(absolute.hostname)) {
      return absolute.toString();
    }

    absolute.protocol = window.location.protocol;
    absolute.host = window.location.host;
    return absolute.toString();
  } catch {
    return raw;
  }
}

export function getApiBaseUrl() {
  if (typeof window !== "undefined" && isDanzfloSiteHost(window.location.hostname)) {
    return `${window.location.origin}/event/api/v2/`;
  }

  const configured = String(process.env.REACT_APP_API_BASE_URL || "").replace(
    /\/?$/,
    "/"
  );
  if (typeof window === "undefined") {
    return configured || "/event/api/v2/";
  }
  return resolveApiUrl(configured || `${window.location.origin}/event/api/v2/`);
}

/** Socket.IO must hit the page origin (nginx proxies /socket.io on danzflo.com). */
export function getSocketUrl() {
  if (typeof window === "undefined") {
    return String(process.env.REACT_APP_BASE_API_URL_FOR_SOCKET || "");
  }
  if (isDanzfloSiteHost(window.location.hostname)) {
    return window.location.origin;
  }
  const configured = String(process.env.REACT_APP_BASE_API_URL_FOR_SOCKET || "");
  if (!configured) {
    return window.location.origin;
  }
  try {
    return new URL(resolveApiUrl(configured), window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
}

/** Merch / platform home (same origin on live; env elsewhere). */
export function getMerchBaseUrl() {
  const configured = String(process.env.REACT_APP_MERCH_URL || "").replace(
    /\/+$/,
    ""
  );
  if (typeof window === "undefined") {
    return configured;
  }
  if (isDanzfloSiteHost(window.location.hostname)) {
    return window.location.origin;
  }
  if (!configured) {
    return window.location.origin;
  }
  return resolveApiUrl(configured).replace(/\/+$/, "");
}
